import React from 'react'
import { Circle } from 'lucide-react'
import { cn } from 'lib/utils'
import renderChatHeaderAvatars from 'features/Chat/utils/renderChatHeaderAvatars'
import type { IAvatars } from 'features/Chat/Chat.types'

export interface Props {
	className?: string
	avatars: IAvatars
	primaryText: string
	secondaryText: string
	isSelected: boolean
	isUnread: boolean
	onClick: any
	/**
	 * 'rail' collapses the row to an avatar-only icon rail between md and lg
	 * (768–1023px). Full rows are restored at lg and below md. Opt-in so the
	 * Home dashboard chat cards keep full rows at every width.
	 */
	variant?: 'default' | 'rail'
}

const ChatHeader = ({
	className = '',
	avatars,
	onClick,
	isSelected = false,
	primaryText,
	secondaryText,
	isUnread = false,
	variant = 'default',
}: Props) => {
	const isRail = variant === 'rail'

	return (
		<div data-testid="ChatHeader" className={className}>
			<div
				className={cn(
					'flex items-center px-4 py-2 min-h-[4.25rem] cursor-pointer transition-colors',
					isSelected ? 'bg-blue-50' : 'hover:bg-gray-50',
					isRail && 'md:px-2 md:justify-center lg:px-4 lg:justify-start',
				)}
				onClick={onClick}
				title={isRail ? primaryText : undefined}
			>
				<div className={cn('relative flex -space-x-3 mr-4 flex-shrink-0', isRail && 'md:mr-0 lg:mr-4')}>
					{renderChatHeaderAvatars(avatars)}
					{isRail && isUnread && (
						<span
							aria-hidden="true"
							className="hidden md:block lg:hidden absolute -top-0.5 -right-0.5 w-2.5 h-2.5 rounded-full bg-blue-500 ring-2 ring-white"
						/>
					)}
				</div>
				<div className={cn('flex-1 min-w-0', isRail && 'md:hidden lg:block')}>
					<div className="text-sm font-medium text-gray-900 truncate">{primaryText}</div>
					<span className="block overflow-hidden text-ellipsis whitespace-nowrap text-xs text-gray-500">
						{secondaryText}
					</span>
				</div>
				{isUnread && (
					<Circle
						className={cn(
							'w-3 h-3 fill-blue-500 text-blue-500 ml-2 flex-shrink-0',
							isRail && 'md:hidden lg:block',
						)}
					/>
				)}
			</div>
		</div>
	)
}

export default ChatHeader
